import * as Sentry from "@sentry/react";
import { EventStatus } from "app/utils/const";
import { difference, uniq, uniqBy } from "lodash";
import cloneDeep from "lodash/cloneDeep";
import has from "lodash/has";
import isEmpty from "lodash/isEmpty";
import IncidentsHelper from "../helpers/incidentsHelper";
import store from "../store/store";
import type {
  BetSlipType,
  BetType,
  GroupedBetSlipsState,
  GroupedBetSlipState,
} from "../types/betSlipType";
import type { EventIncidentType, EventType } from "../types/eventType";
import { BetSlipStates, BetslipStatus } from "../utils/const";
export default class BetDrawerLogic {
  /**
   * Update all bet slips with newest event data from API
   * @param event: fetched from API
   */
  static updateBetSlipsFromEvent(event: EventType) {
    const betSlipList = cloneDeep(
      store.getState().betSlipListModel.betSlipList
    );

    // if event status closed remove from betlipList
    if (event.status === EventStatus.CLOSED) {
      let betIndex = betSlipList.findIndex(betslip => betslip.id === event.id);
      betSlipList.splice(betIndex, 1);
    }

    // Go through each bet slip and
    // and update the event and its market and runner
    betSlipList.forEach(betSlip => {
      // make a clone for each loop
      let clonedEvent = cloneDeep(event);
      // existing bet slip market and runner
      let oldBetSlipMarket = betSlip.markets[0];
      let oldBetSlipRunner = betSlip.markets[0].runners[0];

      //
      if (!isEmpty(clonedEvent.markets)) {
        // find the newly fetched market
        if (clonedEvent.markets === undefined) return;
        let foundMarket = clonedEvent.markets.find(
          market => market.id === oldBetSlipMarket.id
        );

        if (foundMarket != null && !isEmpty(foundMarket.runners)) {
          // find the newly fetched runner
          let foundRunner = foundMarket.runners.find(
            runner => runner.id === oldBetSlipRunner.id
          );

          if (foundRunner != null) {
            // Set the new runner to have the same odds meta as before
            foundRunner.oddsShortened = oldBetSlipRunner.oddsShortened;
            foundRunner.oddsImproved = oldBetSlipRunner.oddsImproved;
            foundRunner.timeout = oldBetSlipRunner.timeout;
            foundMarket.runners = [foundRunner];
            clonedEvent.markets = [foundMarket];
            // Merge new changes
            Object.assign(betSlip, clonedEvent);
          }
        }
      }
    });
    store.dispatch.betSlipListModel.updateBetSlipList({
      betSlipList,
    });
  }

  /**
   * Update selected bet slip in list with newest runner data from Web socket runner update
   *
   */
  static updateBetSlipsFromWebSocketRunner(
    runners: Array<Record<string, any>>
  ) {
    let runnerFoundInBetslip = false; // if runners not in the betslip, dont need to update the store
    let runnerFoundInMultiples = false;

    const betSlipList = cloneDeep(
      store.getState().betSlipListModel.betSlipList
    );
    betSlipList.forEach(betSlip => {
      // check each betslip to see if there is an update
      runners.forEach(runner => {
        if (
          has(betSlip, ["markets", [0], "runners", [0], "id"]) &&
          has(runner, ["id"]) &&
          runner.id === betSlip.markets[0].runners[0].id
        ) {
          runnerFoundInBetslip = true;
          Object.assign(betSlip, runner);
        }
      });
    });

    // Check if runner exists in multiples list
    const multiplesBetSlipList = cloneDeep(
      store.getState().multiplesBetSlipListModel.multiplesBetSlipList
    );
    multiplesBetSlipList.forEach(multiplesBetSlip => {
      runners.forEach(runner => {
        if (multiplesBetSlip["runnerId"] === runner.id) {
          runnerFoundInMultiples = true;
        }
      });
    });
    // Validate multiples to update odds if runner exists in multiples
    if (runnerFoundInMultiples) {
      store.dispatch.multiplesBetSlipListModel.validateMultiples();
    }

    if (runnerFoundInBetslip) {
      store.dispatch.betSlipListModel.updateBetSlipList({
        betSlipList,
      });
    }
  }

  /**
   * Update a bet slip from a bet (API/websocket)
   * @param bet: fetched bet
   */
  static updateBetSlipFromBet(bet: BetType) {
    // For updating status etc of bet
    // bet comes from api/ws
    // check betSlipList

    if (bet["submittedType"] === "MULTIPLIER") {
      if (bet.betBuilder) {
        store.dispatch.betBuilderModel.updateBetBuilderBet(bet);
      } else {
        store.dispatch.multiplesBetSlipListModel.updateMultiplesBetSlip({
          requestedStake: bet["requestedStake"],
          stake: bet.stake,
          status: bet.status,
          profit: bet.profit,
        });
      }
    } else {
      let betSlipList = cloneDeep(
        store.getState().betSlipListModel.betSlipList
      );
      let user = cloneDeep(store.getState().userModel.user);
      let previousBetSlip = betSlipList.find(
        betSlip => betSlip.markets[0].runners[0].id === bet["runnerId"]
      );
      Sentry.captureMessage(
        `WS Bet Status update status:${bet.status} betId:${bet.id} stake:${bet.stake} runnerID:${bet["runnerId"]} user:${user.username} testing`
      );

      // Update the betSlipList
      if (previousBetSlip != null) {
        // Add bet status as state to bet slip
        previousBetSlip.state = bet.status;
        // Add bet id as betId to bet slip
        previousBetSlip.betId = bet.id;

        // BET-4928 update betslip with incremental processing of pending bet
        if (bet.status === "PENDING") {
          previousBetSlip["requestedStake"] = bet["requestedStake"];
          previousBetSlip.stake = bet.stake;
        }

        // Check if the bet failed
        if (bet.status === BetSlipStates.FAILED) {
          const message = bet.message != null ? bet.message : null;
          previousBetSlip.hasError = true;
          previousBetSlip.errorMessage = message;
          previousBetSlip.isSubmitDisabled = true;
        }

        // Update the bet slip
        store.dispatch.betSlipListModel.updateBetSlip(previousBetSlip);

        if (previousBetSlip.state === BetSlipStates.FINISHED) {
          store.dispatch.reportListModel.addReport(bet);
          store.dispatch.competitionListModel.setHasBetsOnEvent({
            eventId: bet["eventId"],
            hasBetsOnEvent: true,
          });
        }
      }
    }
  }

  /**
   * Collapse all the bet slips in the bet drawer
   */
  static collapseAllBetSlips() {
    let betSlipList = cloneDeep(store.getState().betSlipListModel.betSlipList);
    // Set all bet isActive to false
    betSlipList.forEach(bet => (bet.isActive = false));
    //
    BetDrawerLogic.expandCollapseAll(true);
    // Update the bet slip list
    store.dispatch.betSlipListModel.updateBetSlipList({
      betSlipList,
    });

    store.dispatch.betBuilderModel.collapseAllCollapsibles();
  }

  /**
   * Expand all the bet slips in the bet drawer
   */
  static expandAllBetSlips() {
    let betSlipList = cloneDeep(store.getState().betSlipListModel.betSlipList);
    // Set all bet isActive to false
    betSlipList.forEach(bet => (bet.isActive = true));
    //
    BetDrawerLogic.expandCollapseAll(false);
    // Update the bet slip list
    store.dispatch.betSlipListModel.updateBetSlipList({
      betSlipList,
    });
    store.dispatch.betBuilderModel.expandAllCollapsibles();
  }

  /**
   * update grouped bet slip states in the bet drawer to collapse all or expand all
   */
  static expandCollapseAll(collapsed: boolean) {
    // get number of collapsed grouped events from the store
    let noOfBetSlipGroupdedEvents = cloneDeep(
      store.getState().betSlipListModel.noOfBetSlipGroupdedEvents
    );
    // update individual event collapsed state
    noOfBetSlipGroupdedEvents.map((event: GroupedBetSlipState) => {
      return (event.collapsed = collapsed);
    });
    // save updated state to the store
    return store.dispatch.betSlipListModel.updateBetSlipGroupedEvents(
      noOfBetSlipGroupdedEvents
    );
  }

  /**
   * function for keeping track of bet slip grouped events collapse/expand states
   */
  static updateBetSlipGroupedEvents() {
    const { eventIds, noOfBetSlipGroupdedEvents } =
      store.getState().betSlipListModel;
    const uniqEvents = uniq(eventIds);
    // if noOfBetSlipGroupdedEvents is empty populate with grouped events
    // otherwise update with added grouped event
    if (noOfBetSlipGroupdedEvents.length === 0) {
      const options = uniqEvents.map(eventId => ({
        collapsed: false,
        id: eventId,
      }));
      store.dispatch.betSlipListModel.updateBetSlipGroupedEvents(options);
    } else {
      const listOfIds = noOfBetSlipGroupdedEvents.map(
        groupedEvent => groupedEvent.id
      );
      // check difference and use as new grouped event id
      const newAddedEvent = difference(uniqEvents, listOfIds);
      // if new grouped event id added update in store
      if (!isEmpty(newAddedEvent)) {
        const updatedGroupedEvents = [
          ...noOfBetSlipGroupdedEvents,
          { collapsed: false, id: newAddedEvent[0] },
        ];
        // update store with new grouped event
        store.dispatch.betSlipListModel.updateBetSlipGroupedEvents(
          updatedGroupedEvents
        );
      }
    }
  }
  /**
   * remove bet slip grouped event from noOfBetSlipGroupdedEvents
   */
  static removeBetSlipFromBetSlipGroupedEvents(betSlipId: number) {
    const { eventIds, noOfBetSlipGroupdedEvents } =
      store.getState().betSlipListModel;
    // count how many eventIds with the same id as betSlipId are left
    const betslipCount = eventIds.filter(
      eventId => eventId === betSlipId
    ).length;
    // if one left (betslipCount == 0) remove betslip grouped event with the same id
    if (betslipCount === 0) {
      const updatedNoOfBetSlipGroupdedEvents = noOfBetSlipGroupdedEvents.filter(
        event => event.id !== betSlipId
      );
      // update store with updated grouped events
      store.dispatch.betSlipListModel.updateBetSlipGroupedEvents(
        updatedNoOfBetSlipGroupdedEvents
      );
      // after update check if all the bet slip grouped events are either fully expanded or fully collapsed
      BetDrawerLogic.checkIfAllGroupedEventsAreExpandedCollapsed(
        updatedNoOfBetSlipGroupdedEvents
      );
    }
  }
  /**
   * groups all bets from same event into single grouped event
   */
  static groupBetSlipListByEvents(betSlipList: BetSlipType[]) {
    // iterate over betsliplist creating an array of arrays, grouped by id
    // presevers the order that bets were added to the betsliplist originally
    // If more than one bet on an event, then the first bet added will determine
    // where the bet is displayed in the list of bets
    const grouped = betSlipList.reduce<BetSlipType[][]>((acc, obj) => {
      const group = acc.find(group => group[0].id === obj.id);
      if (group) {
        group.push(obj);
      } else {
        acc.push([obj]);
      }
      return acc;
    }, []);

    let groupedEventsCount = uniqBy(betSlipList, "id").length;
    let betSlipsGroupedByEventCount: number = cloneDeep(
      store.getState().betSlipListModel.betSlipsGroupedByEventCount
    );
    // update bet slip grouped by events count if changed
    if (betSlipsGroupedByEventCount !== groupedEventsCount) {
      store.dispatch.betSlipListModel.updateBetSlipsGroupedByEventCount(
        groupedEventsCount
      );
    }

    return grouped;
  }

  /**
   * checks if all the grouped bet slip events are either fully expanded or fully collapsed
   */
  static checkIfAllGroupedEventsAreExpandedCollapsed(
    groupedEvents: GroupedBetSlipsState
  ) {
    const collapsedStates = groupedEvents.map(event => event.collapsed);
    const uniqueCollapsedStates = uniq(collapsedStates);
    // if areCollapsedStatesUniq length is one then a single boolean type is unique
    // this means that all the bet slip grouped events are all collapsed or expanded
    if (uniqueCollapsedStates.length === 1) {
      const allCollapsed = uniqueCollapsedStates[0];
      BetDrawerLogic.expandCollapseAll(allCollapsed);
      store.dispatch.betSlipListModel.collapseOrExpandAllBets(allCollapsed);
    }
  }

  /**
   * toggle bet slip grouped event in the bet drawer to collapse or expand
   */
  static toggleBetSlipGroup(betSlipId: number) {
    // get number of collapsed grouped events
    let noOfBetSlipGroupdedEvents: GroupedBetSlipsState = cloneDeep(
      store.getState().betSlipListModel.noOfBetSlipGroupdedEvents
    );
    // toggle collapsedGroupedBets
    noOfBetSlipGroupdedEvents.map((betSlipEvent: GroupedBetSlipState) => {
      if (betSlipEvent.id === betSlipId) {
        return (betSlipEvent.collapsed = !betSlipEvent.collapsed);
      }
    });
    // check if all betslip grouped events are all expanded/collapsed
    BetDrawerLogic.checkIfAllGroupedEventsAreExpandedCollapsed(
      noOfBetSlipGroupdedEvents
    );
    // update store and  bet slip groupded events state
    store.dispatch.betSlipListModel.toggleNumberOfBetSlipGroupedEvents(
      noOfBetSlipGroupdedEvents
    );
  }

  /**
   * Update betslip when event closes
   * @param event: fetched from API
   */
  static updateBetSlipsFromClosedEvents(eventList: any) {
    const betSlipList = cloneDeep(
      store.getState().betSlipListModel.betSlipList
    );
    eventList.forEach(event => {
      if (event.status === EventStatus.CLOSED) {
        betSlipList.forEach(betslip => {
          if (betslip.id === event.id) {
            betslip.status = EventStatus.CLOSED;
          }
        });
      }

      if (event.status === EventStatus.GRADED) {
        betSlipList.forEach(betslip => {
          if (betslip.id === event.id) {
            betslip.status = BetslipStatus.GRADED;
          }
        });
      }
    });
    store.dispatch.betSlipListModel.updateBetSlipList({
      betSlipList,
    });
  }

  static updatePositionMatrix(event: EventType) {
    const positionMatrixList = cloneDeep(
      store.getState().positionMatrixListModel.positionMatrixList
    );

    if (
      store.positionMatrixListModel.positionMatrixModalItem &&
      store.positionMatrixListModel.positionMatrixModalItem.fullEvent.id ===
        event.id
    ) {
      store.dispatch.positionMatrixListModel.setPositionMatrixModalItem({
        eventId: event.id,
      });
    }

    positionMatrixList.forEach(item => {
      if (event.id === item.eventId) {
        store.dispatch.positionMatrixListModel.setPositionMatrixList();
      }
    });
  }

  static updateEventIncident(eventIncidentUpdatesWS: EventIncidentType[]) {
    let betSlipList: BetSlipType[] = cloneDeep(
      store.getState().betSlipListModel.betSlipList
    );
    eventIncidentUpdatesWS.forEach(eventIncidentUpdateWS => {
      if (betSlipList && betSlipList.length > 0) {
        betSlipList.forEach(betSlip => {
          if (
            betSlip.id === eventIncidentUpdateWS["eventId"] &&
            betSlip["eventIncidents"] != null
          ) {
            IncidentsHelper.updateEventIncidentData(
              betSlip,
              eventIncidentUpdateWS
            );
          }
        });
        store.dispatch.betSlipListModel.updateBetSlipList({
          betSlipList,
        });
      }
    });
  }
}
